import { useMutation, useQuery } from '@tanstack/react-query'
import { RefreshCcw, Send } from 'lucide-react'
import { useState } from 'react'

import { fetchSyncStatus, queueTrademarkSync, type SyncCursor } from '../lib/api'
import { useAuth } from '../lib/auth'

type SyncForm = {
  serials: string
  serialStart: string
  serialEnd: string
  requiredClasses: string
  requiredStatuses: string
}

const defaultSyncForm: SyncForm = {
  serials: '',
  serialStart: '',
  serialEnd: '',
  requiredClasses: '14',
  requiredStatuses: 'LIVE'
}

export function SyncPage() {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'admin'
  const [form, setForm] = useState<SyncForm>(defaultSyncForm)
  const [localError, setLocalError] = useState<string | null>(null)

  const syncQuery = useQuery({
    queryKey: ['sync-status'],
    queryFn: fetchSyncStatus,
    enabled: isAdmin
  })

  const syncMutation = useMutation({
    mutationFn: queueTrademarkSync,
    onSuccess: (queued) => {
      setLocalError(`Queued ${queued.queued_count} sync job${queued.queued_count === 1 ? '' : 's'}`)
      void syncQuery.refetch()
    },
    onError: (error: Error) => {
      setLocalError(error.message)
    }
  })

  const status = syncQuery.data
  const jobCounts = Object.entries(status?.job_counts ?? {}).sort(([left], [right]) =>
    left.localeCompare(right)
  )
  const cursors = status?.recent_cursors ?? []

  function queueSync() {
    const payload = payloadFromForm(form)
    if (!payload) {
      setLocalError('Enter serial numbers or a complete serial range')
      return
    }
    setLocalError(null)
    syncMutation.mutate(payload)
  }

  return (
    <section className="syncPage">
      <div className="toolbar">
        <div>
          <p className="eyebrow">Data source</p>
          <h1>Sync</h1>
        </div>
        <button
          aria-label="Refresh sync status"
          className="secondaryButton"
          type="button"
          onClick={() => {
            void syncQuery.refetch()
          }}
          disabled={!isAdmin || syncQuery.isFetching}
        >
          <RefreshCcw size={18} aria-hidden="true" />
          <span>{syncQuery.isFetching ? 'Refreshing' : 'Refresh'}</span>
        </button>
      </div>

      {!isAdmin ? (
        <section className="syncPanel">
          <div className="settingsEmpty">Admin role required</div>
        </section>
      ) : (
        <>
          <section className="syncPanel" aria-labelledby="sync-queue-title">
            <div className="syncPanelHeader">
              <div>
                <p className="eyebrow">USPTO</p>
                <h2 id="sync-queue-title">Queue Sync</h2>
              </div>
              {localError ? <span className="settingsError">{localError}</span> : null}
            </div>
            <div className="syncQueueForm">
              <label>
                <span>Serials</span>
                <input
                  value={form.serials}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, serials: event.target.value }))
                  }}
                />
              </label>
              <label>
                <span>Range start</span>
                <input
                  value={form.serialStart}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, serialStart: event.target.value }))
                  }}
                />
              </label>
              <label>
                <span>Range end</span>
                <input
                  value={form.serialEnd}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, serialEnd: event.target.value }))
                  }}
                />
              </label>
              <label>
                <span>Classes</span>
                <input
                  value={form.requiredClasses}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, requiredClasses: event.target.value }))
                  }}
                />
              </label>
              <label>
                <span>Status</span>
                <input
                  value={form.requiredStatuses}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, requiredStatuses: event.target.value }))
                  }}
                />
              </label>
              <button
                className="primaryButton"
                type="button"
                onClick={queueSync}
                disabled={syncMutation.isPending}
              >
                <Send size={18} aria-hidden="true" />
                <span>{syncMutation.isPending ? 'Queueing' : 'Queue'}</span>
              </button>
            </div>
          </section>

          <div className="syncSummary">
            <Metric
              label="Status"
              value={syncQuery.error?.message ?? status?.status ?? 'Loading'}
            />
            <Metric label="Job states" value={String(jobCounts.length)} />
            <Metric label="Cursors" value={String(cursors.length)} />
          </div>

          <section className="syncPanel" aria-labelledby="sync-jobs-title">
            <div className="syncPanelHeader">
              <h2 id="sync-jobs-title">Job Counts</h2>
            </div>
            <div className="syncCountGrid">
              {jobCounts.length > 0 ? (
                jobCounts.map(([name, count]) => (
                  <div key={name}>
                    <span>{name}</span>
                    <strong>{count}</strong>
                  </div>
                ))
              ) : (
                <div className="syncEmpty">No sync jobs</div>
              )}
            </div>
          </section>

          <section className="syncPanel" aria-labelledby="sync-cursors-title">
            <div className="syncPanelHeader">
              <h2 id="sync-cursors-title">Recent Cursors</h2>
            </div>
            <div className="cursorList">
              {cursors.length > 0 ? (
                cursors.map((cursor) => <CursorCard key={cursor.source} cursor={cursor} />)
              ) : (
                <div className="syncEmpty">No cursors</div>
              )}
            </div>
          </section>
        </>
      )}
    </section>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function CursorCard({ cursor }: { cursor: SyncCursor }) {
  return (
    <article className="cursorCard">
      <div>
        <span className="compareEyebrow">{cursor.source}</span>
        <strong>{formatDateTime(cursor.updated_at)}</strong>
      </div>
      <pre>{JSON.stringify(cursor.cursor, null, 2)}</pre>
    </article>
  )
}

function payloadFromForm(form: SyncForm) {
  const serialNumbers = form.serials
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean)
  const serialStart = form.serialStart.trim()
  const serialEnd = form.serialEnd.trim()
  const hasRange = serialStart.length > 0 || serialEnd.length > 0
  if (serialNumbers.length === 0 && !hasRange) {
    return null
  }
  if (hasRange && (!serialStart || !serialEnd)) {
    return null
  }
  return {
    serial_numbers: serialNumbers,
    serial_start: serialStart || null,
    serial_end: serialEnd || null,
    required_classes: parseIntegerList(form.requiredClasses, [14]),
    required_status_prefixes: parseStringList(form.requiredStatuses, ['LIVE'])
  }
}

function parseIntegerList(value: string, fallback: number[]): number[] {
  const parsed = value
    .split(/[\s,]+/)
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item))
  return parsed.length > 0 ? parsed : fallback
}

function parseStringList(value: string, fallback: string[]): string[] {
  const parsed = value
    .split(/[\s,]+/)
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean)
  return parsed.length > 0 ? parsed : fallback
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}
